import { useEffect, useState } from "react"
import { v4 as uuid } from "uuid"
import { useDispatch, useSelector } from "react-redux"

// action
import { setErrorMessage, setSuccessMessage } from "Containers/Messages/actions"

// Utils
import { getCors, postCors } from "Utils/rulesCors"
import { config } from "Utils/config"
import request from "Utils/request"

export function useDashboardData(
  dashboardId: string,
  token: string,
  isFineganBusiness: boolean = false,
  options: { skip?: boolean } = {}
) {
  const skip = options.skip ?? false
  const dispatch = useDispatch()

  // State
  const [fields, setFields] = useState<any>([])
  const [widgets, setWidgets] = useState<any>([])
  const [widgetData, setWidgetData] = useState<Record<string, any[]>>({})
  const [loading, setLoading] = useState<Boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false) // <— nouvel état

  // Fetch champs
  const fetchFieldData = async () => {
    try {
      const headers = getCors({ Authorization: `Bearer ${token}` })
      const url = `${config.APOLLO_SERVER_URL}/etl/dashboard/${dashboardId}/fields`
      const response = await request(url, headers)
      if (response?.type === "error") {
        setError(response.message || "Erreur API")
      } else {
        setFields(response.body || [])
      }
    } catch (err) {
      setError((err as Error).message || "Erreur inconnue")
    }
  }

  const fetchWidget = async () => {
    try {
      const headers = getCors({ Authorization: `Bearer ${token}` })
      const url = `${config.APOLLO_SERVER_URL}/etl/dashboard/${dashboardId}/widgets`
      const response = await request(url, headers)

      if (response?.type === "error") {
        setError(response.message || "Erreur API")
      } else {
        setWidgets(response || [])
      }
    } catch (err) {
      setError((err as Error).message || "Erreur inconnue")
      dispatch(setErrorMessage({ message: (err as Error).message || "Erreur inconnue" }))
    }
  }

  const postFineganData = async () => {
    try {
      const headers = getCors({ token })
      const url = `${config.FORM_BACK_URL}/saveDataReferentialFinegan`
      const response = await request(url, headers)
      if (response?.type === "error") {
        setError(response.message || "Erreur API")
      }
    } catch (err) {
      setError((err as Error).message || "Erreur inconnue")
      dispatch(setErrorMessage({ message: (err as Error).message || "Erreur inconnue" }))
    }
  }

  useEffect(() => {
    if (skip) return
    if (!dashboardId || dashboardId === "") return
    fetchFieldData()
    fetchWidget()
  }, [dashboardId, token, skip])

  // Fetch données individuelles pour chaque widget
  useEffect(() => {
    if (skip) return
    if (!dashboardId || widgets.length === 0) return

    const fetchAllWidgetData = async () => {
      setLoading(true)
      try {
        const newWidgetData: Record<string, any[]> = {}

        for (const widget of widgets) {
          const requestId = uuid()
          const body = {
            ...widget.request,
            requestId,
          }

          const headers = postCors(body, { Authorization: `Bearer ${token}` })
          const url = `${config.APOLLO_SERVER_URL}/etl/dashboard/${dashboardId}/request`
          const response = await request(url, headers)

          if (response?.type === "error") {
            console.warn(`Erreur sur widget ${widget.id}:`, response.message)
            newWidgetData[widget.id] = []
          } else {
            newWidgetData[widget.id] = response.body || []
          }
        }

        setWidgetData(newWidgetData)
      } catch (err) {
        console.error("Erreur globale:", err)
        setError((err as Error).message || "Erreur inconnue")
        dispatch(setErrorMessage({ message: (err as Error).message || "Erreur inconnue" }))
      } finally {
        setLoading(false)
      }
    }

    fetchAllWidgetData()
  }, [dashboardId, widgets, token, skip])

  // 🔁 PATCH /dashboard/:dashboardId/widget/:widgetId
  const updateWidget = async (widgetId: string, widgetData: any) => {
    if (!dashboardId || dashboardId === "") return
    try {
      const url = `${config.APOLLO_SERVER_URL}/etl/dashboard/${dashboardId}/widget/${widgetId}`
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(widgetData),
      })

      if (response.ok) {
        dispatch(
          setSuccessMessage({ message: "Succès lors de la mise a jour de la configuration" })
        )
      } else {
        dispatch(setErrorMessage({ message: `Erreur ${response.status}: ${response.statusText}` }))
      }
    } catch (err) {
      console.error("Erreur updateWidget:", err)
      setError((err as Error).message || "Erreur inconnue")
      dispatch(setErrorMessage({ message: (err as Error).message || "Erreur inconnue" }))
      throw err
    }
  }

  // 🔁 POST /dashboard/:dashboardId/refresh
  const refreshDataDash = async (dashboardId: string) => {
    if (!dashboardId || dashboardId === "" || isRefreshing) return

    setIsRefreshing(true)
    setError(null)
    try {
      if (isFineganBusiness) {
        await postFineganData()
      }
      const url = `${config.APOLLO_SERVER_URL}/etl/dashboard/${dashboardId}/refresh`
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        dispatch(setSuccessMessage({ message: "Succès lors de la mise a jour des données" }))
        // On relance les fetchs de config ; le useEffect widgets se chargera de recharger les données
        await fetchFieldData()
        await fetchWidget()
      } else {
        dispatch(setErrorMessage({ message: `Erreur ${response.status}: ${response.statusText}` }))
      }
    } catch (err) {
      console.error("Erreur refreshDataDash:", err)
      setError((err as Error).message || "Erreur inconnue")
      dispatch(setErrorMessage({ message: (err as Error).message || "Erreur inconnue" }))
      throw err
    } finally {
      setIsRefreshing(false)
    }
  }

  return {
    fields,
    widgets,
    widgetData,
    loading,
    error,
    isRefreshing,
    updateWidget,
    refreshDataDash,
  }
}

export function getFields(dashboardId: string, token: string) {
  // State
  const [fields, setFields] = useState([])
  const [referentialId, setReferentialId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Fetch champs
  const fetchFieldData = async () => {
    try {
      const headers = getCors({ Authorization: `Bearer ${token}` })
      const url = `${config.APOLLO_SERVER_URL}/etl/dashboard/${dashboardId}/fields`
      const response = await request(url, headers)
      if (response?.type === "error") {
        setError(response.message || "Erreur API")
      } else {
        setFields(response.body || [])
        if (response.referentialId) setReferentialId(response.referentialId)
      }
    } catch (err) {
      setError((err as Error).message || "Erreur inconnue")
    }
  }
  useEffect(() => {
    if (!dashboardId || dashboardId === "") return
    fetchFieldData()
  }, [dashboardId, token])

  return {
    fields,
    referentialId,
    error,
  }
}
